import { useCallback, useRef, useState } from 'react'
import { Button, Input, Space, Spin, theme } from 'antd'
import type { InputRef } from 'antd'
import { CheckOutlined, EyeOutlined, SendOutlined, StarOutlined, WarningOutlined } from '@ant-design/icons'
import { BacktestChat } from '@/api'
import { createUuid } from '@/utils/uuid'
import type { Msg, BacktestFormData } from '@/types'
import '@/styles/aiChat.scss'
import './AIChat.scss'

const signals = [
    { id: 1, type: 1, title: '收现比异常', content: '连续3季低于预值0.90' },
    { id: 2, type: 2, title: '分红回购提升', content: '回购金额+45% YoY' },
    { id: 3, type: 3, title: '商誉调整风险', content: '调整标的估值偏高警觉' },
]

export default function AIChat({ backtestFormData = {} }: { backtestFormData?: Partial<BacktestFormData> }) {
    const { token } = theme.useToken()
    const [sessionId] = useState(() => createUuid())
    const [messages, setMessages] = useState<Msg[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const chatBodyRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)

    const scrollBottom = () =>
        setTimeout(() => {
            const chatBody = chatBodyRef.current
            if (!chatBody) return
            chatBody.scrollTo({
                top: chatBody.scrollHeight,
                behavior: 'smooth',
            })
        }, 50)

    const send = useCallback(async () => {
        const text = input.trim()
        if (!text || loading) return
        const userMsg: Msg = { session_id: sessionId, role: 'user', message: text, time: new Date().toLocaleTimeString() }
        setMessages((prev) => [...prev, userMsg])
        setInput('')
        setLoading(true)
        scrollBottom()
        try {
            const { response } = await BacktestChat({
                session_id: sessionId,
                message: text,
                context: backtestFormData,
            })
            setMessages((prev) => [
                ...prev,
                { session_id: sessionId, role: 'assistant', message: response, time: new Date().toLocaleTimeString() },
            ])
            scrollBottom()
        } finally {
            setLoading(false)
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [input, loading])

    const handleClear = () => {
        setMessages([])
    }

    return (
        <div className='ai-chat-container'>
            {/* Header */}
            <div
                className='ai-chat-header'
            >
                <div className='ai-chat-header-title'>
                    <StarOutlined style={{ color: '#3b82f6' }} />AI分析助手<div className='ai-dot'></div>
                </div>
                <Button size="small" className='text-10' onClick={handleClear}>清空</Button>
            </div>

            {/* Chat area */}
            <div className='ai-chat-body' ref={chatBodyRef}>
                <div className='ai-chat-body-content'>
                    <section className='ai-chat-body-content-section'>
                        <div className='ai-chat-body-content-title text-10 color-3'>智能摘要</div>
                        <div className='ai-chat-body-content-sub text-12 color-2 flex items-center flex-between'>
                            <div className='ai-chat-body-content-sub-title flex items-center gap-4'>
                                <svg width="13" style={{ color: '#3b82f6' }} height="13" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                                综合研判
                            </div>
                            <Button size="small" className='text-11' style={{ background: 'var(--green-dim)', color: 'var(--green)', border: '1px solid rgba(34, 197, 94, .3)' }}>买入</Button>
                        </div>
                        <div className='ai-chat-body-content-content text-12 color-2' style={{ marginTop: 12, marginBottom: 12 }}>
                            宁德时代基本面稳健，全球占率持续领先。短期关注收现比异常信号，中期估值有支撑。目标价区间185-210元。
                            <div className='ai-chat-body-content-sub-content'>
                                <div className='ai-chat-body-content-sub-content-item'>
                                    <div className='ai-chat-body-content-sub-content-item-title'>
                                        基本面
                                    </div>
                                    <div className='ai-chat-body-content-sub-content-item-content green'>
                                        85
                                    </div>
                                </div>
                                <div className='ai-chat-body-content-sub-content-item'>
                                    <div className='ai-chat-body-content-sub-content-item-title'>
                                        风险度
                                    </div>
                                    <div className='ai-chat-body-content-sub-content-item-content green'>
                                        38
                                    </div>
                                </div>
                                <div className='ai-chat-body-content-sub-content-item'>
                                    <div className='ai-chat-body-content-sub-content-item-title'>
                                        估值性价
                                    </div>
                                    <div className='ai-chat-body-content-sub-content-item-content orange'>
                                        72
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className='ai-chat-body-content-section'>
                        <div className='ai-chat-body-content-title text-10 color-3'>触发信号</div>
                        {
                            signals.map((signal) => (
                                <div className={`ai-chat-body-content-signal flex items-center gap-8 signal-${signal.type}`} key={signal.id}>
                                    {
                                        signal.type === 1 ? <WarningOutlined className='red' /> : signal.type === 2 ? <CheckOutlined className='green' /> : <EyeOutlined className='warning' />
                                    }
                                    <div className='ai-chat-body-content-signal-title text-13 color-1 font-weight-600'>
                                        {signal.title}
                                        <div className='ai-chat-body-content-signal-content text-10 color-3'>{signal.content}</div>
                                    </div>
                                </div>
                            ))
                        }
                    </section>

                    <section className='ai-chat-body-content-section ai-chat-body-content-section-emotion'>
                        <div className='ai-chat-body-content-title text-10 color-3'>情绪指标</div>
                        <div className='ai-chat-body-content-emotion-content text-10 color-3' style={{ marginTop: 12, marginBottom: 12 }}>
                            <div className='ai-chat-body-content-emotion-content-item'>
                                <div className='ai-chat-body-content-emotion-content-item-title'>
                                    机构情绪
                                </div>
                                <div className='ai-chat-body-content-emotion-content-item-content accent font-weight-700'>
                                    78
                                </div>
                                偏多头
                            </div>
                            <div className='ai-chat-body-content-emotion-content-item'>
                                <div className='ai-chat-body-content-emotion-content-item-title'>
                                    基础情绪
                                </div>
                                <div className='ai-chat-body-content-emotion-content-item-content warning font-weight-700'>
                                    52
                                </div>
                                中性偏谨慎
                            </div>
                        </div>
                        <div className='ai-chat-body-content-emotion-content-item text-10 color-3' style={{ padding: '8px 12px',marginBottom: 12 }}>机构与草根背离值 +26，关注分歧</div>
                    </section>

                    <section className='ai-chat-body-content-section ai-chat-body-content-section-instruction'>
                        <div className='ai-chat-body-content-title text-10 color-3'>指令驱动分析</div>
                        <div className='ai-chat-body-content-instruction-content flex items-center gap-8 flex-wrap' style={{ marginTop: 12, marginBottom: 12 }}>
                            {
                                ['财务三表对比','估值横向对比','行业竞争格局','海外扩张进展'].map((ele) => (
                                    <Button className='ai-chat-body-content-instruction-content-item text-11' style={{ color: 'var(--text-2)' }} key={ele}>
                                        {ele}
                                    </Button>
                                ))
                            }
                        </div>
                    </section>
                </div>
                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                        <div style={{ padding: '8px 12px', background: token.colorFillSecondary, borderRadius: 8 }}>
                            <Spin size="small" />
                        </div>
                    </div>
                )}

                {
                    messages.map((msg, index) => (
                        <div key={index} className='ai-chat-body-content-message' style={{justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'}}>
                            {msg.role === 'user' ? msg.message : <div dangerouslySetInnerHTML={{ __html: msg.message }} />}
                            <div className='ai-message-time'>{msg.time}</div>
                        </div>
                    ))
                }
            </div>

            {/* Input bar */}
            <div style={{ padding: '20px', borderTop: `1px solid var(--border)` }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPressEnter={send}
                        placeholder="输入分析指令或问题..."
                        disabled={loading}
                    />
                    <Button type="primary" size="large" icon={<SendOutlined />} onClick={send} loading={loading} />
                </Space.Compact>
            </div>
        </div>
    )
}
